import { useMutation, MutationFunction, MutationOptions } from 'react-query';
import {
    UpdateUserBody,
    UserResponse,
    updateUser,
    CreateUserBody,
    createUser,
    uploadSecurityPassport,
    deleteSecurityPassport,
    changeUserPassword,
    ChangePasswordBody,
} from '../../services/apiSLTP/users';

type UseUpdateUserMutationOptions = MutationOptions<UserResponse, Error, { userId: string; data: UpdateUserBody }>;
type UseCreateUserMutationOptions = MutationOptions<UserResponse, Error, CreateUserBody>;
type UseUploadSecurityPassportOptions = MutationOptions<UserResponse, Error, { userId: string; file: File }>;
type UseDeleteSecurityPassportOptions = MutationOptions<UserResponse, Error, { userId: string }>;

export const useUpdateUserMutation = (options?: UseUpdateUserMutationOptions) => {
    const mutationFn: MutationFunction<UserResponse, { userId: string; data: UpdateUserBody }> = async ({ userId, data }) => {
        try {
            return await updateUser(userId, data);
        } catch (error) {
            throw new Error('An unexpected error occurred during user update');
        }
    };

    return useMutation(mutationFn, options);
};

export const useCreateUserMutation = (options?: UseCreateUserMutationOptions) => {
    const mutationFn: MutationFunction<UserResponse, CreateUserBody> = async (data) => {
        try {
            return await createUser(data);
        } catch (error) {
            throw new Error('An unexpected error occurred during user creation');
        }
    };

    return useMutation(mutationFn, options);
};

export const useUploadSecurityPassportMutation = (options?: UseUploadSecurityPassportOptions) => {
    const mutationFn: MutationFunction<UserResponse, { userId: string; file: File }> = async ({ userId, file }) => {
        try {
            return await uploadSecurityPassport(userId, file);
        } catch (error) {
            throw new Error('An unexpected error occurred during file upload');
        }
    };

    return useMutation(mutationFn, options);
};

export const useDeleteSecurityPassportMutation = (options?: UseDeleteSecurityPassportOptions) => {
    const mutationFn: MutationFunction<UserResponse, { userId: string }> = async ({ userId }) => {
        try {
            return await deleteSecurityPassport(userId);
        } catch (error) {
            throw new Error('An unexpected error occurred during file deletion');
        }
    };

    return useMutation(mutationFn, options);
};

type UseChangeUserPasswordMutationOptions = MutationOptions<UserResponse, Error, { userId: string; body: ChangePasswordBody }>;

export const useChangeUserPasswordMutation = (options?: UseChangeUserPasswordMutationOptions) => {
    const mutationFn: MutationFunction<UserResponse, { userId: string; body: ChangePasswordBody }> = async ({ userId, body }) => {
        try {
            return await changeUserPassword(userId, body);
        } catch (error) {
            throw new Error('An unexpected error occurred during password change');
        }
    };

    return useMutation(mutationFn, options);
};
